/**
 * Task Estimation Historical Service - Phase 4 Architecture
 *
 * Encapsulates historical data analysis, accuracy tracking, and similar tasks.
 * Extracted from TaskEstimationService to reduce single-responsibility violations.
 */

import { typedSupabase } from '@/lib/typed-supabase';
import { logger } from '@/lib/logger';
import {
  IService,
  ServiceResponse,
  createSuccessResponse,
  createErrorResponse,
} from '@/services/core/service-registry';
import type {
  EstimationData,
  EstimationMethod,
  TaskPattern,
} from "@/types/time-management";

export class TaskEstimationHistoricalService implements IService {
  public readonly name = "TaskEstimationHistoricalService";
  private readonly supabase = typedSupabase;

  async initialize(): Promise<void> {
    logger.info("✅ TaskEstimationHistoricalService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ TaskEstimationHistoricalService disposed");
  }

  async getHistoricalData(
    userId: string,
    taskPattern: TaskPattern
  ): Promise<ServiceResponse<EstimationData[]>> {
    try {
      const { data, error } = await this.supabase
        .from("task_estimations")
        .select(
          `
          *,
          todos!inner(
            title,
            description,
            category_id,
            created_by
          )
        `
        )
        .eq("todos.created_by", userId)
        .not("actual_duration", "is", null)
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) {
        return createErrorResponse(error.message);
      }

      const filteredItems = ((data || []) as Array<{
        id: string;
        task_id: string;
        estimated_duration: number;
        actual_duration: number | null;
        estimation_method: string;
        factors: unknown;
        created_at: string | null;
        todos: {
          title: string;
          description: string | null;
          category_id: string | null;
          created_by: string;
        };
      }>).filter((item) => {
        const todo = item.todos;
        return (
          (!taskPattern.category_id ||
            todo.category_id === taskPattern.category_id) &&
          (!taskPattern.similar_keywords ||
            taskPattern.similar_keywords.some((keyword: string) =>
              todo.title.toLowerCase().includes(keyword.toLowerCase())
            ))
        );
      });

      const categoryTasks = filteredItems.filter(
        (item) =>
          !taskPattern.category_id ||
          item.todos.category_id === taskPattern.category_id
      );

      if (categoryTasks.length === 0) {
        return createSuccessResponse([]);
      }

      const historicalDurations = categoryTasks
        .filter((item) => item.actual_duration !== null)
        .map((item) => item.actual_duration as number);

      const averageDuration =
        historicalDurations.length > 0
          ? historicalDurations.reduce((sum, duration) => sum + duration, 0) /
            historicalDurations.length
          : 0;

      const accuracyRate =
        categoryTasks.length > 0
          ? categoryTasks
              .filter((item) => item.actual_duration !== null)
              .map((item) =>
                this.calculateAccuracy(
                  item.estimated_duration,
                  item.actual_duration!
                )
              )
              .reduce((sum, acc) => sum + acc, 0) / historicalDurations.length
          : 0;

      const estimationData: EstimationData = {
        task_pattern: taskPattern,
        historical_durations: historicalDurations,
        average_duration: averageDuration,
        accuracy_rate: accuracyRate,
        confidence_score: historicalDurations.length >= 5 ? 0.8 : 0.5,
      };

      return createSuccessResponse([estimationData]);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async calculateHistoricalAccuracy(
    userId: string,
    timeRange?: { start: string; end: string }
  ): Promise<
    ServiceResponse<{
      overallAccuracy: number;
      averageError: number;
      accuracyByMethod: Record<EstimationMethod, number>;
      accuracyByCategory: Record<string, number>;
      trendingAccuracy: number[];
    }>
  > {
    try {
      let query = this.supabase
        .from("task_estimations")
        .select(
          `
          *,
          todos!inner(
            category_id,
            created_by
          )
        `
        )
        .eq("todos.created_by", userId)
        .not("actual_duration", "is", null);

      if (timeRange) {
        query = query
          .gte("created_at", timeRange.start)
          .lte("created_at", timeRange.end);
      }

      const { data, error } = await query;

      if (error) {
        return createErrorResponse(error.message);
      }

      if (!data || data.length === 0) {
        return createSuccessResponse({
          overallAccuracy: 0,
          averageError: 0,
          accuracyByMethod: {} as Record<EstimationMethod, number>,
          accuracyByCategory: {},
          trendingAccuracy: [],
        });
      }

      const validData = (data as Array<{
        actual_duration: number | null;
        estimated_duration: number;
        estimation_method: string;
        created_at: string | null;
        todos: { category_id: string | null };
      }>).filter((item) => item.actual_duration !== null) as Array<{
        actual_duration: number;
        estimated_duration: number;
        estimation_method: string;
        created_at: string | null;
        todos: { category_id: string | null };
      }>;

      if (validData.length === 0) {
        return createSuccessResponse({
          overallAccuracy: 0,
          averageError: 0,
          accuracyByMethod: {} as Record<EstimationMethod, number>,
          accuracyByCategory: {},
          trendingAccuracy: [],
        });
      }

      const accuracies = validData.map((item) =>
        this.calculateAccuracy(item.estimated_duration, item.actual_duration)
      );
      const overallAccuracy =
        accuracies.reduce((sum, acc) => sum + acc, 0) / accuracies.length;

      const errors = validData.map((item) =>
        Math.abs(item.estimated_duration - item.actual_duration)
      );
      const averageError =
        errors.reduce((sum, err) => sum + err, 0) / errors.length;

      const accuracyByMethod = validData.reduce(
        (acc: Record<string, number[]>, item) => {
          const method = item.estimation_method as EstimationMethod;
          if (!acc[method]) {acc[method] = [];}
          acc[method].push(
            this.calculateAccuracy(
              item.estimated_duration,
              item.actual_duration
            )
          );
          return acc;
        },
        {}
      );

      const methodAverages: Record<EstimationMethod, number> = {} as Record<
        EstimationMethod,
        number
      >;
      Object.keys(accuracyByMethod).forEach((method) => {
        const values = accuracyByMethod[method];
        methodAverages[method as EstimationMethod] =
          values.reduce((sum: number, val: number) => sum + val, 0) /
          values.length;
      });

      const accuracyByCategory = validData.reduce(
        (acc: Record<string, number[]>, item) => {
          const category = item.todos.category_id ?? "uncategorized";
          if (!acc[category]) {acc[category] = [];}
          acc[category].push(
            this.calculateAccuracy(
              item.estimated_duration,
              item.actual_duration
            )
          );
          return acc;
        },
        {}
      );

      const categoryAverages: Record<string, number> = {};
      Object.keys(accuracyByCategory).forEach((category) => {
        const values = accuracyByCategory[category];
        categoryAverages[category] =
          values.reduce((sum: number, val: number) => sum + val, 0) /
          values.length;
      });

      const trendingAccuracy = this.calculateTrendingAccuracy(validData);

      return createSuccessResponse({
        overallAccuracy,
        averageError,
        accuracyByMethod: methodAverages,
        accuracyByCategory: categoryAverages,
        trendingAccuracy,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async getSimilarTasks(
    taskTitle: string,
    userId: string,
    limit = 5
  ): Promise<
    ServiceResponse<
      Array<{
        taskId: string;
        title: string;
        estimatedDuration: number;
        actualDuration: number;
        accuracy: number;
        similarity: number;
      }>
    >
  > {
    try {
      const { data, error } = await this.supabase
        .from("task_estimations")
        .select(
          `
          *,
          todos!inner(
            id,
            title,
            created_by
          )
        `
        )
        .eq("todos.created_by", userId)
        .not("actual_duration", "is", null)
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) {
        return createErrorResponse(error.message);
      }

      const similarTasks = (data || [])
        .filter((item) => (item as { actual_duration: number | null }).actual_duration !== null)
        .map(
          (item: {
            todos: { id: string; title: string };
            estimated_duration: number;
            actual_duration: number | null;
          }) => ({
            taskId: item.todos.id,
            title: item.todos.title,
            estimatedDuration: item.estimated_duration,
            actualDuration: item.actual_duration!,
            accuracy: this.calculateAccuracy(
              item.estimated_duration,
              item.actual_duration!
            ),
            similarity: this.calculateSimilarity(taskTitle, item.todos.title),
          })
        )
        .filter((task) => task.similarity > 0.3)
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, limit);

      return createSuccessResponse(similarTasks);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async trainUserModel(userId: string): Promise<
    ServiceResponse<{
      modelVersion: string;
      accuracy: number;
      trainingDataSize: number;
      features: string[];
    }>
  > {
    try {
      const historicalResult = await this.getHistoricalData(userId, {
        similar_keywords: [],
        category_id: undefined,
        priority: "medium",
        complexity_indicators: [],
      });

      if (historicalResult.error) {
        return createErrorResponse(historicalResult.error);
      }

      const trainingData = historicalResult.data!;

      if (trainingData.length < 10) {
        return createErrorResponse("Need at least 10 completed tasks with actual durations for training");
      }

      const averageAccuracy =
        trainingData.length > 0
          ? trainingData.reduce((sum, item) => sum + item.accuracy_rate, 0) /
            trainingData.length
          : 0;

      const modelVersion = `v${Date.now()}-${userId.slice(-6)}`;
      const features = [
        "task_title_length",
        "task_complexity",
        "category_type",
        "similar_tasks_avg",
        "user_historical_accuracy",
      ];

      logger.info(
        `🧠 Training model for user ${userId} with ${trainingData.length} data points`
      );

      return createSuccessResponse({
        modelVersion,
        accuracy: averageAccuracy,
        trainingDataSize: trainingData.length,
        features,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async getEstimationInsights(
    userId: string,
    period: "week" | "month" | "quarter"
  ): Promise<
    ServiceResponse<{
      totalEstimations: number;
      averageAccuracy: number;
      bestMethod: EstimationMethod;
      worstCategories: string[];
      recommendations: string[];
    }>
  > {
    try {
      const periodDays = period === "week" ? 7 : period === "month" ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - periodDays);

      const accuracyResult = await this.calculateHistoricalAccuracy(userId, {
        start: startDate.toISOString(),
        end: new Date().toISOString(),
      });

      if (accuracyResult.error) {
        return createErrorResponse(accuracyResult.error);
      }

      const data = accuracyResult.data!;

      const bestMethod = Object.entries(data.accuracyByMethod).reduce(
        (best, [method, accuracy]) =>
          accuracy > best.accuracy
            ? { method: method as EstimationMethod, accuracy }
            : best,
        { method: "manual" as EstimationMethod, accuracy: 0 }
      ).method;

      const worstCategories = Object.entries(data.accuracyByCategory)
        .filter(([_, accuracy]) => accuracy < 0.7)
        .sort(([_, a], [__, b]) => a - b)
        .slice(0, 3)
        .map(([category]) => category);

      const recommendations = this.generateRecommendations(
        data,
        bestMethod,
        worstCategories
      );

      return createSuccessResponse({
        totalEstimations: data.trendingAccuracy.length,
        averageAccuracy: data.overallAccuracy,
        bestMethod,
        worstCategories,
        recommendations,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  private calculateAccuracy(estimated: number, actual: number): number {
    if (estimated === 0 || actual === 0) {return 0;}
    const ratio = Math.min(estimated, actual) / Math.max(estimated, actual);
    return Math.round(ratio * 100) / 100;
  }

  private calculateSimilarity(title1: string, title2: string): number {
    const words1 = title1
      .toLowerCase()
      .split(" ")
      .filter((w) => w.length > 2);
    const words2 = title2
      .toLowerCase()
      .split(" ")
      .filter((w) => w.length > 2);

    if (words1.length === 0 || words2.length === 0) {return 0;}

    const commonWords = words1.filter((word) => words2.includes(word));
    const similarity =
      commonWords.length / Math.max(words1.length, words2.length);

    return Math.round(similarity * 100) / 100;
  }

  private calculateTrendingAccuracy(
    data: Array<{
      estimated_duration: number;
      actual_duration: number;
      created_at: string | null;
    }>
  ): number[] {
    const now = new Date();
    const trends: number[] = [];

    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);

      const dayData = data.filter((item) => {
        if (!item.created_at) {return false;}
        const itemDate = new Date(item.created_at);
        return itemDate.toDateString() === date.toDateString();
      });

      if (dayData.length > 0) {
        const avgAccuracy =
          dayData.reduce(
            (sum, item) =>
              sum +
              this.calculateAccuracy(
                item.estimated_duration,
                item.actual_duration
              ),
            0
          ) / dayData.length;
        trends.push(avgAccuracy);
      } else {
        trends.push(0);
      }
    }

    return trends;
  }

  private generateRecommendations(
    data: {
      overallAccuracy: number;
      averageError: number;
      accuracyByMethod: Record<EstimationMethod, number>;
      accuracyByCategory: Record<string, number>;
      trendingAccuracy: number[];
    },
    bestMethod: EstimationMethod,
    worstCategories: string[]
  ): string[] {
    const recommendations: string[] = [];

    if (data.overallAccuracy < 0.7) {
      recommendations.push(
        "Focus on improving estimation accuracy - consider breaking down complex tasks"
      );
    }

    if (bestMethod !== "ai_suggested") {
      recommendations.push(
        `Your best estimation method is ${bestMethod} - consider using it more often`
      );
    }

    if (worstCategories.length > 0) {
      recommendations.push(
        `Pay special attention to ${worstCategories.join(", ")} categories - they have lower accuracy`
      );
    }

    if (data.averageError > 60) {
      recommendations.push(
        "Consider using smaller time increments for more accurate estimates"
      );
    }

    return recommendations;
  }
}
